import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, defer, map, tap } from 'rxjs';
import { OverlayTeardownService } from 'src/app/core/services/overlay-teardown.service';
import { PushNotificationService } from 'src/app/core/push/push-notification.service';
import {
  OFFLINE_SESSION_ENABLED,
  browserReportsOffline,
  clearLogoutPending,
  forgetOfflineIdentity,
  isNetworkError,
  markLogoutPending,
  rememberOfflineIdentity,
} from 'src/app/core/offline/offline-identity';
import { forgetUserOfflineData } from 'src/app/core/offline/offline-user-data';
import { OfflineModeState } from 'src/app/core/offline/offline-mode.state';
import { State } from 'src/app/store/auth/auth.reducers';
import { BaseAction, SessionActionType } from 'src/app/store/store.actions';
import { environment } from 'src/environments/environment';
import { EMPTY_AUTH } from '../models/auth';
import { buildAuthToken, decodeAuthToken, DecodedAuthToken } from '../utils/jwt-to-auth';
import { AccessTokenHolder } from './access-token-holder.service';
import { AuthSyncService } from './auth-sync.service';
import { TfaType } from './two-factor.service';

/**
 * CSRF model -- read me before adding new cookie-authed POSTs.
 *
 * <p>/v2/login, /v2/refresh, /v2/logout, /v2/logout-all are state-changing POSTs that the
 * backend authenticates via the {@code refresh_token} httpOnly cookie. These endpoints are on
 * the backend's {@code ignored.posts} list (Spring Security CSRF filter bypassed) and no CSRF
 * (XSRF) token is exchanged in either direction: the frontend does NOT mount
 * {@code HttpClientXsrfModule}, and the backend does not check a token.
 *
 * <p>The PRIMARY CSRF defense is the cookie's {@code SameSite=Strict} attribute (emitted by
 * the lib's RefreshCookieFactory along with HttpOnly + Secure). A cross-site POST originating
 * from {@code attacker.com} cannot include the refresh cookie under Strict, so the backend
 * sees the request as unauthenticated and rejects it.
 *
 * <p>This is defense-in-depth-of-one. If the cookie is ever relaxed to {@code SameSite=Lax}
 * (to fix a top-level navigation issue) or {@code None} (for embedding), the only remaining
 * CSRF barrier disappears silently. Adding any new cookie-authed POST endpoint here -- or
 * loosening the cookie attribute anywhere -- requires either (a) keeping {@code SameSite=Strict}
 * intact across every environment, or (b) reinstating a real CSRF token exchange.
 *
 * <p>The 2FA-challenge POSTs /v2/login/2FA and /v2/login/2FA/resend send {@code withCredentials}
 * too, but their actual principal is the unguessable, short-lived {@code challengeToken} in the
 * request BODY, not the cookie -- a cross-site forgery gains nothing without that token. They are
 * listed here for completeness of the cookie-authed POST surface; the SameSite argument above still
 * covers them, and the same rule applies before adding another.
 *
 * <p>Tests pin the cookie attributes: see {@code RefreshTokenIntegrationTest.refreshCookieValue}
 * (Secure + HttpOnly + Path=/v2/ + SameSite=Strict) in galactus-core's integration tests.
 */

export type LoginOutcome =
  | { kind: 'authenticated' }
  | { kind: 'enroll' }
  | { kind: 'twoFactor'; challengeToken: string; method: TfaType; codeDelivered: boolean };

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loginUrl = `${environment.apiUrl}/v2/login`;
  private logoutUrl = `${environment.apiUrl}/v2/logout`;
  private logoutAllUrl = `${environment.apiUrl}/v2/logout-all`;

  constructor(
    private httpClient: HttpClient,
    private store: Store<State>,
    private router: Router,
    private authSync: AuthSyncService,
    private tokenHolder: AccessTokenHolder,
    private overlayTeardown: OverlayTeardownService,
    private push: PushNotificationService,
    private offlineMode: OfflineModeState,
    @Inject(OFFLINE_SESSION_ENABLED) private offlineSessionEnabled: boolean,
  ) { }

  login(username: string, password: string): Observable<LoginOutcome> {
    return this.httpClient
      .post(this.loginUrl, { username, password }, { observe: 'response', withCredentials: true })
      .pipe(map((resp: HttpResponse<any>) => this.handleLoginResponse(resp)));
  }

  /**
   * Second factor: submit the challenge token issued by the first-factor 202 plus the user's code.
   * No username/password re-send — the backend recovers the principal from the (signed, short-lived)
   * challenge token. A wrong code surfaces as a 401 via the global interceptor.
   */
  loginTwoFactor(challengeToken: string, code: string): Observable<LoginOutcome> {
    return this.httpClient
      .post(`${environment.apiUrl}/v2/login/2FA`, { challengeToken, code }, { observe: 'response', withCredentials: true })
      .pipe(map((resp: HttpResponse<any>) => this.handleLoginResponse(resp)));
  }

  /**
   * Re-deliver the OTP for the in-progress MAIL/SMS login challenge (AUTH has no server-sent code to
   * resend -- LoginComponent hides the button for it). Mirrors loginTwoFactor's call shape (challenge
   * token only, cookie-authed like the rest of the /v2/login family) but there is no outcome to route on
   * -- a 204 just confirms a fresh code went out against the SAME challenge token, so this returns void.
   */
  resendChallenge(challengeToken: string): Observable<void> {
    return this.httpClient
      .post(`${environment.apiUrl}/v2/login/2FA/resend`, { challengeToken }, { withCredentials: true })
      .pipe(map(() => void 0));
  }

  private handleLoginResponse(resp: HttpResponse<any>): LoginOutcome {
    const body = resp.body || {};
    if (resp.status === 202) {
      // The challenge token is the first-factor receipt; the code step returns it (not the password).
      // Guard it like the 201 token below: without it, submitCode() would POST an empty challenge and
      // the user would be stranded on an unwinnable code step with a misleading "wrong code" message.
      if (!body['challengeToken']) {
        throw new Error('Two-factor login response missing challenge token');
      }
      // The 202 also carries which second-factor method the user must complete (tfatype: 'AUTH'|'MAIL'|
      // 'SMS'|'WHATSAPP'). LoginComponent needs it to decide whether to offer the Resend affordance
      // (MAIL/SMS/WHATSAPP only -- AUTH has no server-sent code to resend). Guard/default rather than
      // throw: the backend should always send it, but a resend button wired up for an unrecognised method
      // is worse than none, so an absent/unexpected value falls back to 'AUTH' (no resend offered)
      // instead of stranding the user.
      // codeDelivered is ABSENT on success: the backend leaves it null and omits null fields, so a
      // successful challenge body is byte-identical to what this client already handled. Only an
      // explicit false means the code never went out -- reading undefined as false would put a failure
      // banner on every successful login.
      //
      // Compared as a lower-cased STRING, with the same leniency the enrollment flag below is read with
      // (String(x) === 'true'): a backend that serialises the flag as the string "false" means the code
      // did not go out just as much as the boolean does, and a strict `!== false` would silently read
      // that as delivered and hide the banner -- the one failure mode this whole field exists to make
      // visible. `undefined` stringifies to "undefined", so an absent field still reads as delivered.
      // Lower-cased because a case-sensitive compare accepts the leniency and then throws it away for
      // "False"/"FALSE" (what a .NET-style or hand-rolled serialiser emits) -- which is the banner
      // hidden, i.e. exactly the failure being guarded against, at the one spelling nobody would test.
      //
      // The body's companion `deliveryErrorMessage` is deliberately NOT surfaced. The code step is a
      // PRE-AUTHENTICATED screen reachable by anyone who knows a username, so whatever the backend puts
      // in that string would be readable there; and it is server text that bypasses ngx-translate, so an
      // Italian UI could render an English/provider-worded sentence. The failure is binary as far as the
      // user's next action goes ("use a backup code"), so the banner uses a local i18n string. If
      // per-cause copy is ever wanted, map the numeric `deliveryErrorCode` to a key here rather than
      // printing the server's prose.
      return {
        kind: 'twoFactor',
        challengeToken: body['challengeToken'],
        method: this.parseTfaType(body['tfatype']),
        codeDelivered: String(body['codeDelivered']).toLowerCase() !== 'false',
      };
    }
    // A 201 must carry a token; guard so a malformed response surfaces as a clear contract
    // error rather than a cryptic jwtDecode(undefined) crash inside applyToken.
    if (!body['token']) {
      throw new Error('Login response missing token');
    }
    // Honour BOTH enrollment signals: the response body flag (the documented primary signal) AND the
    // token's own enrollment claim. The claim is what actually scopes the bearer — OR them so a
    // body/token contract skew can never admit a restricted enrollment token into /app as a full
    // session. The cookie-refresh path already trusts the token claim (decodeAuthToken), so login must
    // agree; otherwise the two paths could classify the same token differently.
    // Decode the JWT ONCE here and hand the result to applyToken so the token isn't parsed twice.
    const decoded = decodeAuthToken(body['token']);
    // Parse the body flag with the SAME leniency as the token claim (jwt-to-auth: String(x)==='true'),
    // so a backend that serialises the flag as the string "true" is honoured here too rather than only
    // via the token claim. OR-ed with the claim, which alone already scopes the bearer.
    const enrollmentRequired = String(body['enrollment']) === 'true' || decoded.enrollment;
    this.applyToken(body['token'], enrollmentRequired, decoded);
    return { kind: enrollmentRequired ? 'enroll' : 'authenticated' };
  }

  /** Guard/cast the 202 body's `tfatype` to a known TfaType, defaulting to 'AUTH' (see handleLoginResponse). */
  private parseTfaType(value: unknown): TfaType {
    return value === 'AUTH' || value === 'MAIL' || value === 'SMS' || value === 'WHATSAPP' ? value : 'AUTH';
  }

  /**
   * Apply a raw JWT: store the bearer envelope in the in-memory holder, push the
   * non-secret identity slice to the NgRx store, and broadcast a login event to
   * other tabs via AuthSyncService. PUBLIC so the enrollment wizard can call it
   * after obtaining a token at the end of the setup flow.
   *
   * `enrollmentRequired` is OR-ed with the token's own enrollment claim: a caller that knows the flag
   * (the login response signals it out-of-band) can RAISE it, but can never CLEAR it against a token
   * that still claims enrollment. Omit it and the flag is the token's claim alone (the cookie-refresh
   * path has only the token). This mirrors handleLoginResponse's defensive OR so a restricted
   * enrollment token can never be admitted as a full session — including via EnrollComponent.finish(),
   * which passes `false`: that clears the flag ONLY when the freshly-issued token agrees (claim absent);
   * on a backend contract skew (token still claiming enrollment) the claim wins and the user stays
   * confined to the wizard rather than reaching /app with a route-scoped bearer. The identity and the
   * flag are applied ATOMICALLY via one action (both
   * reducers key off it — see {@link SessionActionType}) so a watcher reacting to `isAuthenticated`
   * (LoginComponent) can never see the new identity alongside a stale flag and poach navigation from
   * the forced-enrollment flow. The value is broadcast so sibling tabs stay consistent.
   *
   * `decoded` lets a caller that already parsed this exact JWT (handleLoginResponse) pass the result
   * so the token is decoded ONCE, not twice; omit it and the token is decoded here.
   */
  public applyToken(rawJwt: string, enrollmentRequired?: boolean, decoded?: DecodedAuthToken): void {
    // Decode the token ONCE for both the identity slice and the enrollment claim (reuse the caller's
    // decode when supplied). Decode BEFORE swapping the in-memory bearer: if decodeAuthToken throws on a
    // malformed token, the holder must still carry the PREVIOUS token. Swapping first would leave the new
    // full-access bearer in the holder while the NgRx auth/enrollment slices stay unchanged — trapping a
    // forced-enrollment user on /auth/enroll (store still says enrollmentRequired) with every subsequent
    // request carrying the full bearer. EnrollComponent.finish() calls this with an undecoded token and
    // only resets `finishing` in its catch, so it relies on the holder being untouched on failure.
    const { auth: authResp, enrollment } = decoded ?? decodeAuthToken(rawJwt);
    this.tokenHolder.set(buildAuthToken(rawJwt));
    // OR the token's own enrollment claim with the caller's assertion: the claim can only RAISE the
    // flag, never be silently overridden by a caller passing `false` (see the applyToken doc). Guards
    // the same invariant handleLoginResponse states — a token still claiming enrollment is never
    // admitted as a full session.
    const enrollReq = enrollment || (enrollmentRequired ?? false);
    this.store.dispatch(new BaseAction(SessionActionType.APPLY_SESSION, { auth: authResp, enrollmentRequired: enrollReq }));
    this.authSync.notifyLogin(authResp, enrollReq);
    // A new login replaces the refresh cookie a deferred logout was about: completing that logout at
    // the next boot would now sign THIS session out.
    clearLogoutPending();
    // The identity an offline boot may fall back on (offline-identity.ts). Written AFTER the apply, so
    // it never describes a session the store does not hold; an enrollment-scoped token clears it.
    if (this.offlineSessionEnabled) {
      rememberOfflineIdentity(authResp, enrollReq);
    }
  }

  /**
   * User-initiated logout: best-effort server-side revocation of THIS device's refresh family,
   * followed by a local clear. Returns the underlying HTTP Observable so callers can surface
   * failure to the user (consistent with {@link login}); the local clear runs in either branch
   * (server-side revocation can fail and we still want the local session gone). Callers MUST
   * subscribe -- the Observable is cold and won't run otherwise.
   */
  logout(): Observable<unknown> {
    // INSIDE the defer, so it runs on SUBSCRIBE rather than when the Observable is built. The
    // method is documented as cold and callers are told they must subscribe; a side effect at
    // build time breaks that promise, and this particular side effect tears down push delivery
    // for a session that then carries on (a confirmation dialog the user cancels, a piped chain
    // that is discarded -- neither of which exists today, and both of which would fail silently).
    //
    // BEFORE the POST, not after it via clearSession(). The DELETE it fires is authenticated with
    // the bearer, and /v2/logout revokes this device's refresh family server-side -- so a removal
    // issued afterwards races a session the server has already begun tearing down and comes back
    // 401. That 401 is silent (the endpoint is carved out of the interceptor's refresh-retry, by
    // design: a heartbeat POST that refreshed on 401 would re-feed itself), so the failure shows
    // up as nothing at all -- and the row survives. The row surviving is the whole problem: on a
    // SHARED terminal it is what keeps the next person's lock screen quiet, and without the DELETE
    // it lives until the 60-minute TTL sweep; on a PERSONAL device explicit logout is the ONLY
    // thing that removes it, so it would live until the push service 404/410s it.
    return defer(() => {
      this.push.removeCurrentSubscription();
      this.deferLogoutIfOffline();
      return this.httpClient.post(this.logoutUrl, {}, { withCredentials: true });
    }).pipe(
      tap({
        next: () => {
          clearLogoutPending();
          this.clearSession();
        },
        error: (e) => {
          this.deferLogoutIfUnsent(e);
          this.clearSession();
        }
      })
    );
  }

  /**
   * An offline tab cannot revoke its refresh cookie: the interceptor refuses /v2/logout in offline
   * mode, and with no network the POST fails with status 0. The local teardown still runs, so record
   * the logout for the next boot to complete (see `markLogoutPending`); otherwise that boot's refresh
   * would silently sign the user who logged out back in. Recorded BEFORE the POST, so the record
   * exists even if the page is closed while the request is still failing.
   */
  private deferLogoutIfOffline(): void {
    if (this.offlineMode.active || browserReportsOffline()) {
      markLogoutPending();
    }
  }

  /** The POST never reached a server (status 0, offline mode's refusal included): same reason. */
  private deferLogoutIfUnsent(error: unknown): void {
    if (isNetworkError(error)) {
      markLogoutPending();
    }
  }

  /**
   * "Log out from every device" -- revokes EVERY refresh-token family this user owns server-side
   * (every browser, every device), then clears the local session. Other tabs/devices will fail
   * their next /v2/refresh and be bounced to login. Uses the refresh cookie like {@link logout()}.
   * Returns the underlying Observable so the Settings UI can surface a failure ("could not
   * sign other devices out") instead of falsely claiming success after a network/server error.
   */
  logoutEverywhere(): Observable<unknown> {
    // Same shape as logout(): deferred so the teardown is a subscribe-time effect, and issued
    // BEFORE the POST. The ordering matters more here -- /v2/logout-all bumps
    // base_user.tokens_valid_from to now, which invalidates every bearer this user holds
    // IMMEDIATELY, including the one in this tab's holder. A removal issued after the POST is
    // therefore guaranteed to 401, not merely likely to.
    //
    // Offline it defers like logout(): the next boot posts /v2/logout, which revokes at least THIS
    // device's family. The other devices are not signed out -- that needs the user online, and the
    // Settings UI already reports this call's failure.
    return defer(() => {
      this.push.removeCurrentSubscription();
      this.deferLogoutIfOffline();
      return this.httpClient.post(this.logoutAllUrl, {}, { withCredentials: true });
    }).pipe(
      tap({
        next: () => {
          clearLogoutPending();
          this.clearSession();
        },
        error: (e) => {
          this.deferLogoutIfUnsent(e);
          this.clearSession();
        }
      })
    );
  }

  /**
   * Local-only session teardown: clear NgRx auth state, broadcast logout to other
   * tabs, and navigate to /auth. Makes NO HTTP call, so it is safe to invoke from
   * the HTTP interceptor on a 401 without risking a /v2/logout request loop
   * (logout() posts to /v2/logout, which is itself a 401-capable auth endpoint).
   */
  clearSession() {
    // Dismiss any open overlay FIRST — before the navigation below, or the dialog is already painted
    // over the login page when the router swaps. The CDK overlay container is appended to <body> — a
    // sibling of <app-root> — so navigateByUrl('/auth') only swaps the router-outlet's content and
    // leaves the dialog and its backdrop on screen. Most dialogs here are `disableClose: true`
    // (2FA/security, table edit), so ESC and the backdrop can't dismiss them either and the user's only
    // escape is a full reload. Covers the sticky error toast raised by the 401 too. Dialogs registered
    // via OverlayTeardownService.exemptFromTeardown (the one-time 2FA backup codes) deliberately stay.
    this.overlayTeardown.closeAll();
    // Drop this device's push subscription while the outgoing user's bearer is still in the
    // holder -- the backend identifies the row by the calling user. Fire-and-forget: clearSession
    // is synchronous and cannot await, and is also reached from the interceptor's 401 path, so the
    // DELETE is carved out of 401 handling and cannot re-enter this teardown. Without it, pushes
    // keep arriving on a lock screen for a session that has already ended.
    //
    // KEPT even though logout()/logoutEverywhere() now remove it BEFORE their POST: this method is
    // also the teardown for paths that never went through either -- the interceptor's 401 bounce,
    // the cross-tab logout branch, and the wizard's back-to-login. The user-initiated path calling
    // it twice costs nothing, because the second call finds the delivery half already dropped with
    // nothing registered since and issues no request -- see `deliveryRemoved` in
    // PushNotificationService. That guard is why this is safe on the logoutEverywhere() path in
    // particular, where the second DELETE would go out after tokens_valid_from has been bumped and
    // so is a guaranteed 401 the interceptor carve-out swallows without a word.
    this.push.removeCurrentSubscription();
    this.tokenHolder.clear();
    // Teardown is a session apply too: clear the identity AND the forced-2FA-enrollment flag in ONE atomic
    // action (both reducers key off APPLY_SESSION). The enrollment flag is session state, so a teardown
    // (logout, "back to login" from the wizard, or a 401 bounce) must reset it — otherwise a stale
    // enrollmentRequired survives and the enrollment guards mis-route the next login. Using the single
    // shared action (rather than a separate FULL + clear pair) keeps the two slices consistent by
    // construction, with no dispatch ordering to get wrong.
    this.store.dispatch(new BaseAction(SessionActionType.APPLY_SESSION, { auth: EMPTY_AUTH, enrollmentRequired: false }));
    // Every teardown (logout, logout-all, the 401 bounce, back-to-login) ends the offline identity:
    // the session it stood for is over, and an unverifiable identity must not outlive it. Removing
    // the shared key also ends offline mode in every OTHER tab (OfflineSessionService's storage
    // listener). The offline CATALOGUE is kept -- it is company data, not this user's (CLAUDE.md,
    // "Offline mode"). Exit BEFORE the navigation, or the offline guard on /auth would bounce it.
    forgetOfflineIdentity();
    // ...but the user-scoped offline data (Scan's offline quotes: our prices, stock and sales) goes,
    // like the phone app's on logout (core/offline/offline-user-data.ts).
    forgetUserOfflineData();
    this.offlineMode.exit();
    this.authSync.notifyLogout();
    this.router.navigateByUrl('/auth').then();
  }
}
